/**
 * Botón flotante de WhatsApp (sections/whatsapp-button.liquid).
 *
 * - El botón abre/cierra el panel de motivos de contacto. Sin motivos, abre WhatsApp directo.
 * - La burbuja proactiva aparece tras `data-delay` segundos. Cerrarla, decir "No" o "Sí"
 *   la descarta por el resto de la visita (sessionStorage).
 * - Con `data-vambe-client-id`, el enlace pasa por el redirect de Vambe; si no, va a wa.me.
 * - En móvil, se sube para no tapar la barra fija de "Agregar al carrito" del producto.
 */

const DISMISSED_KEY = 'wsp_proactive_dismissed';
const STICKY_BAR_GAP = 12;

const session = {
  /** @param {string} key */
  get(key) {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  /** @param {string} key @param {string} value */
  set(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      /* modo privado o storage bloqueado: la burbuja solo no se recuerda */
    }
  },
};

class WhatsAppButton extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {number | undefined} */
  #timer;
  /** @type {MutationObserver | undefined} */
  #stickyObserver;

  connectedCallback() {
    this.toggle = /** @type {HTMLButtonElement} */ (this.querySelector('[data-wsp-toggle]'));
    this.options = /** @type {HTMLElement} */ (this.querySelector('[data-wsp-options]'));
    this.proactive = /** @type {HTMLElement | null} */ (this.querySelector('[data-wsp-proactive]'));
    if (!this.toggle || !this.options) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.toggle.addEventListener('click', this.#onToggleClick, { signal });
    this.options.addEventListener('click', this.#onOptionClick, { signal });
    this.proactive?.addEventListener('click', this.#onProactiveClick, { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal });
    document.addEventListener('keydown', this.#onKeydown, { signal });

    if (this.proactive && !this.#isDismissed()) {
      const delay = Number(this.dataset.delay ?? 4) * 1000;
      this.#timer = window.setTimeout(() => this.showProactive(), delay);
    }

    this.#watchStickyAddToCart(signal);

    if (window.Shopify?.designMode) this.#listenToEditor(signal);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#stickyObserver?.disconnect();
    window.clearTimeout(this.#timer);
  }

  get hasOptions() {
    return this.options.querySelector('[data-message]') !== null;
  }

  get optionsOpen() {
    return !this.options.hidden;
  }

  /** @param {string} [message] */
  openWhatsApp(message = '') {
    const phone = this.dataset.phone ?? '';
    const clientId = this.dataset.vambeClientId;

    const url = clientId
      ? `https://api.vambe.ai/api/redirect/website/to-whatsapp?phoneTo=${phone}&message=${encodeURIComponent(
          message
        )}&clientId=${encodeURIComponent(clientId)}`
      : `https://wa.me/${phone.replace(/\D/g, '')}${message ? `?text=${encodeURIComponent(message)}` : ''}`;

    window.open(url, '_blank', 'noopener');
  }

  openOptions() {
    this.hideProactive(true);
    this.options.hidden = false;
    this.toggle.setAttribute('aria-expanded', 'true');
  }

  closeOptions() {
    if (!this.optionsOpen) return;
    this.options.hidden = true;
    this.toggle.setAttribute('aria-expanded', 'false');
  }

  /** @param {{ force?: boolean }} [options] `force` ignora el descarte (vista previa del editor). */
  showProactive({ force = false } = {}) {
    if (!this.proactive) return;
    if (!force && (this.#isDismissed() || this.optionsOpen)) return;
    if (force) this.closeOptions();
    this.proactive.classList.add('is-visible');
  }

  /** @param {boolean} [remember] */
  hideProactive(remember = false) {
    window.clearTimeout(this.#timer);
    if (!this.proactive?.classList.contains('is-visible')) return;
    this.proactive.classList.remove('is-visible');
    if (remember && !window.Shopify?.designMode) session.set(DISMISSED_KEY, '1');
  }

  #isDismissed() {
    return !window.Shopify?.designMode && session.get(DISMISSED_KEY) === '1';
  }

  /** @param {MouseEvent} event */
  #onToggleClick = (event) => {
    event.stopPropagation();

    if (!this.hasOptions) {
      this.hideProactive(true);
      this.openWhatsApp();
      return;
    }

    if (this.optionsOpen) this.closeOptions();
    else this.openOptions();
  };

  /** @param {MouseEvent} event */
  #onOptionClick = (event) => {
    const option = /** @type {HTMLElement | null} */ (
      /** @type {Element} */ (event.target).closest('[data-message]')
    );
    if (!option) return;
    this.openWhatsApp(option.dataset.message);
    this.closeOptions();
  };

  /** @param {MouseEvent} event */
  #onProactiveClick = (event) => {
    const target = /** @type {Element} */ (event.target);

    if (target.closest('[data-wsp-accept]')) {
      const shown = this.proactive?.querySelector('.wsp-proactive__msg')?.textContent?.trim() ?? '';
      this.openWhatsApp(this.proactive?.dataset.wa?.trim() || shown);
      this.hideProactive(true);
    } else if (target.closest('[data-wsp-dismiss]')) {
      this.hideProactive(true);
    }
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!this.contains(/** @type {Node} */ (event.target))) this.closeOptions();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;

    if (this.optionsOpen) {
      this.closeOptions();
      this.toggle.focus();
    } else if (this.proactive?.classList.contains('is-visible')) {
      this.hideProactive(true);
    }
  };

  /**
   * La barra fija de "Agregar al carrito" (sections/product-information.liquid) ocupa todo el
   * ancho abajo en móvil. Mientras se ve y se cruza con el botón, el botón sube por encima.
   * @param {AbortSignal} signal
   */
  #watchStickyAddToCart(signal) {
    const host = document.querySelector('sticky-add-to-cart');
    const bar = /** @type {HTMLElement | null} */ (host?.querySelector('.sticky-add-to-cart__bar'));
    if (!host || !bar) return;

    const update = () => {
      let lift = 0;
      const visible = bar.dataset.stuck === 'true' && host.getAttribute('data-variant-available') === 'true';

      if (visible) {
        const barRect = bar.getBoundingClientRect();
        const buttonRect = this.toggle.getBoundingClientRect();
        const overlapsHorizontally = barRect.left < buttonRect.right && barRect.right > buttonRect.left;

        if (overlapsHorizontally) {
          const barBottom = parseFloat(getComputedStyle(bar).bottom) || 0;
          lift = barBottom + bar.offsetHeight + STICKY_BAR_GAP;
        }
      }

      this.style.setProperty('--wsp-lift', `${lift}px`);
    };

    this.#stickyObserver = new MutationObserver(update);
    this.#stickyObserver.observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
    this.#stickyObserver.observe(host, { attributes: true, attributeFilter: ['data-variant-available'] });
    window.addEventListener('resize', update, { signal, passive: true });
    update();
  }

  /**
   * En el editor: al seleccionar la sección se muestra la burbuja al toque, y al seleccionar
   * una opción de contacto se abre el panel, para poder ver los cambios sin esperar.
   * @param {AbortSignal} signal
   */
  #listenToEditor(signal) {
    const isThisSection = (/** @type {Event} */ event) =>
      /** @type {CustomEvent} */ (event).detail?.sectionId === this.dataset.sectionId;

    document.addEventListener(
      'shopify:section:select',
      (event) => {
        if (isThisSection(event)) this.showProactive({ force: true });
      },
      { signal }
    );

    document.addEventListener(
      'shopify:section:deselect',
      (event) => {
        if (!isThisSection(event)) return;
        this.hideProactive();
        this.closeOptions();
      },
      { signal }
    );

    document.addEventListener(
      'shopify:block:select',
      (event) => {
        if (isThisSection(event) && this.options.contains(/** @type {Node} */ (event.target))) this.openOptions();
      },
      { signal }
    );
  }
}

if (!customElements.get('whatsapp-button')) {
  customElements.define('whatsapp-button', WhatsAppButton);
}
